import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { tenantService } from '../services/api';
import { DailyGuest } from '../types';
import { guestBalanceDue, guestStayDays } from '../utils/dailyGuestDates';
import './GuestPayModal.css';

export type GuestPayModalProps = {
  guest: DailyGuest | null;
  onClose: () => void;
  onSuccess: () => void;
};

const GuestPayModal: React.FC<GuestPayModalProps> = ({ guest, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const balance = useMemo(() => (guest ? guestBalanceDue(guest) : 0), [guest]);

  useEffect(() => {
    if (guest && balance > 0) {
      setAmount(String(balance));
    } else {
      setAmount('');
    }
  }, [guest, balance]);
  const days = useMemo(
    () => (guest ? guestStayDays(guest.checkInDate, guest.checkOutDate) : 0),
    [guest],
  );
  const rate = guest ? Number(guest.dailyRate) || 0 : 0;
  const paid = guest ? Number(guest.amountPaid) || 0 : 0;
  const total = Math.round(days * rate * 100) / 100;

  if (!guest) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pay = Number(amount);
    if (!Number.isFinite(pay) || pay <= 0) {
      toast.error('Enter a valid payment amount');
      return;
    }
    if (balance <= 0) {
      toast.error('No balance due on this stay');
      return;
    }
    const applied = Math.min(pay, balance);
    try {
      setSaving(true);
      await tenantService.updateDailyGuest(guest.id, {
        amountPaid: Math.round((paid + applied) * 100) / 100,
      });
      toast.success(`Payment of ₹${applied.toLocaleString('en-IN')} recorded`);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="guest-pay-backdrop" role="presentation" onClick={onClose}>
      <div
        className="guest-pay-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-pay-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="guest-pay-modal__head">
          <h3 id="guest-pay-title">Record payment</h3>
          <button type="button" className="guest-pay-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="guest-pay-modal__summary">
          <p>
            <strong>{guest.name}</strong>
            <span className="guest-pay-modal__phone">{guest.phoneNumber}</span>
          </p>
          <div className="guest-pay-modal__grid">
            <span>Stay total</span>
            <strong>₹{total.toLocaleString('en-IN')}</strong>
            <span>Paid so far</span>
            <strong>₹{paid.toLocaleString('en-IN')}</strong>
            <span>Balance due</span>
            <strong className={balance > 0 ? 'guest-pay-modal__due' : ''}>
              ₹{balance.toLocaleString('en-IN')}
            </strong>
          </div>
        </div>

        {balance > 0 ? (
          <form className="guest-pay-modal__form" onSubmit={(e) => void handleSubmit(e)}>
            <label>
              Payment amount (₹)
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={String(balance)}
                required
                autoFocus
              />
            </label>
            <button
              type="button"
              className="guest-pay-modal__link"
              onClick={() => setAmount(String(balance))}
            >
              Pay full balance (₹{balance.toLocaleString('en-IN')})
            </button>
            <div className="guest-pay-modal__actions">
              <button type="button" className="guest-pay-modal__btn guest-pay-modal__btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="guest-pay-modal__btn guest-pay-modal__btn--primary" disabled={saving}>
                {saving ? 'Saving…' : 'Record payment'}
              </button>
            </div>
          </form>
        ) : (
          <div className="guest-pay-modal__paid">
            <p>This stay is fully paid.</p>
            <button type="button" className="guest-pay-modal__btn guest-pay-modal__btn--primary" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestPayModal;
